import axios from 'axios';
import { getCharityId } from './charity';
import 'url-search-params-polyfill';
import config from '../../public/harness_config.json';
import ApiRequest from './api-request';
import { getDevBypass } from './devByPass';
import auth from '@/auth/authService';
import { objectToFormData } from './utils';
import $store from '@/store/index';
import { captureException } from "@sentry/vue";
import { getToken } from './token';

const apiRequest = new ApiRequest(config);

const moment = require('moment');

const apiUrl = `${config.scheme}://${config.url}/${config.api_version}`; //'https://staging.harnessapp.com/v2.0';

/**
 * curl -X POST https://staging.harnessapp.com/v2.0/test/pusher -H 'Content-Type: application/x-www-form-urlencoded' -d 'event_name=inbound-sms&charity_id=clay&uid=1613&message=test-msg'
 * @param charityId
 * @returns {Pusher}
 */
function getPusher(charityId = false) {
  charityId = charityId ? charityId : getCharityId();
  // eslint-disable-next-line
  return new Pusher(config.pusher.app_key, {
    cluster: config.pusher.cluster,
    forceTLS: true,
    authEndpoint: `${apiUrl}/charity/${charityId}/websocket`,
    auth: {
      headers: {
        'X-HARNESS-JWT': auth.getToken(),
      },
    },
  });
}

/**
 * GET /v2.0/engage/:charityId/outbound
 */
function getOutbox(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/engage/${charityId}/outbound`,
    {},
    {
      'X-Harness-JWT': getToken(),
    },
    { skipCache: true },
  );
}

/**
 * return all possible metadata fields in a charity
 * @param charityId
 * @returns {Promise}
 */
function getMetadataColumns(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  const cacheKey = `getMetadataColumns_${charityId}`;

  if (localStorage.getItem(cacheKey) !== null) {
    let dInfo = JSON.parse(localStorage.getItem(cacheKey));

    //cache
    if (moment().subtract(15, 'minutes') < moment(dInfo.date)) {
      return new Promise((resolve) => {
        resolve(dInfo['data']);
      });
    }
  }

  return apiRequest.doGetRequest(
    `/data/${charityId}/metadata/columns`,
    {},
    { 'X-Harness-JWT': getToken() },
    {
      skipCache: true,
      resCallback: (res) => {
        if (res.columns.length) {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ date: new Date(), data: res }),
          );
        }

        return res;
      },
    },
  );
}

/**
 * upload csv contacts
 * /v2.0/charity/:charityId/import
 */
function uploadContacts(file, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new FormData();

    params.append('csv', file);

    let endpoint = `${apiUrl}/charity/${charityId}/import`;
    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * invite an admin
 * POST /v2.0/charity/:id/client
 */
function inviteAdmin(email, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('jwt', getToken());
    params.append('email', email);

    return axios
      .post(`${apiUrl}/charity/${charityId}/admin`, params)
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Remove a user from a list
 * DELETE /v2.0/charity/:charityId/lists/:listId/users/:uid
 */
function removeUserFromList(listId, userId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/charity/${charityId}/lists/${listId}/users/${userId}`;

    axios
      .delete(endpoint, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Get all users that are NOT in a list
 */
function getUsersForList(listId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/lists/${listId}/users/available`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Add users to a list
 * POST /v2.0/charity/:charityId/lists/:listId/users
 * This request accepts one or more UIDs of users to add. A single user
 * may be added by simply supplying a uid parameter.
 * Multiple users may be added by using array formatted uid[] parameters.
 * For example, to add both users 1093 and 1094:
 */
function addUsersToList(listId, userIds, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/charity/${charityId}/lists/${listId}/users`;

    let params = {
      userIds: userIds,
    };

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/json',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Create a new list oe edit a list's name and description
 * POST /v2.0/charity/:charityId/lists/
 * name: The client's chosen name for the list.
 * description: (Optional) The description of the list.
 */
function createList(name, description, listId = false, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('name', name);
    params.append('description', description);

    let endpoint = `${apiUrl}/charity/${charityId}/lists/`;

    if (listId) {
      endpoint += listId;
    }

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Delete a list
 * DELETE /v2.0/charity/:charityId/lists/:listId
 */
function deleteList(listId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    axios
      .delete(apiUrl + '/charity/' + charityId + '/lists/' + listId, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Get a specific list
 */
function getOneList(listId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/lists/${listId}`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Get all Lists
 */
function getLists(charityId) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/lists/`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}


// /**
//  *
//  * @param charityId
//  * @returns {Promise}
//  */
// function getCharityConfig(charityId = false) {
//   charityId = charityId ? charityId : getCharityId();
//   return apiRequest.doGetRequest(`/whitelabel/${charityId}/config`, {jwt: getToken()}, {}, {skipCache: true});
// }

/**
 * GET Widget Config
 * @param charityId
 * @returns {Promise}
 */
function getWidgetConfig(charityId = false) {
  charityId = charityId ? charityId : getCharityId();
  return apiRequest.doGetRequest(
    `/charity/${charityId}/widget`,
    { jwt: getToken() },
    {},
    { skipCache: true },
  );
}

/**
 * POST Update Donation Widget Config
 * {{url}}/charity/:charityId/widget
 *
 * @param params
 * @param charityId
 * @returns {Promise}
 */
function updateWidgetConfig(params, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/charity/${charityId}/widget`;

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/json',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * GET /v2.0/data/charity/:id/subdomain
 * @param charityId
 * @returns {Promise}
 */
function getCharitySubdomain(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(`/charity/${charityId}/subdomain`, {
    jwt: getToken(),
  });
}

/**
 * Get secret API key
 * @param charityId
 * @returns {Promise}
 */
function getSecretApiKey(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/api-secret`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

const EmployerMatchingConfig = {
  /**
   * Get Employer Matching Config
   * @param charityId
   * @returns {Promise}
   */
  get(charityId = false) {
    charityId = charityId ? charityId : getCharityId();

    return apiRequest.doGetRequest(
      `/charity/${charityId}/settings/integrations`,
      {},
      { 'X-Harness-JWT': getToken() },
      { skipCache: true },
    );
  },
  /**
   * Update Employer Matching Config
   * @param charityId
   * @returns {Promise}
   */
  update(publicKey, privateKey, charityId = false) {
    charityId = charityId ? charityId : getCharityId();

    let params = new URLSearchParams();
    params.append('public_key', publicKey); // TODO: this can be removed once production is updated to use headers
    params.append('private_key', privateKey);

    return apiRequest.doPostRequest(
      `/charity/${charityId}/settings/integrations`,
      params,
      {
        'X-Harness-JWT': getToken(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    );
  },
};

function checkTasks(charityId) {
  let token = getToken();
  return apiRequest.doGetRequest(
    `/charity/${charityId}/tasks`,
    { jwt: getToken() },
    {},
    {
      resCallback: (r) => {
        let hasConfig = Boolean(r.has_whitelabel_config);
        let hasStripe = Boolean(r.has_stripe_connect);
        let hasSub = Boolean(r.chosen_subscription);

        let devBypass = getDevBypass();

        //check if any required tasks are missing
        if (!(hasConfig && hasStripe && hasSub) && !devBypass) {
          //set JWT to key for onboarding
          localStorage.clientData = token;

          if (window.location.pathname !== '/onboard/tasks') {
            window.location.href = `/onboard/tasks?charity_id=${charityId}`;
          }
        }
        return r;
      },
    },
  );
}

/**
 * GET client/charities
 * {{url}}/client/charities
 *
 * @returns {Promise}
 */
function getCharities() {
  return apiRequest.doGetRequest(
    `/client/charities`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

function addPost(title, body, type, url, id = false) {
  if (type !== 'video') {
    type = 'image';
  }
  let params = new URLSearchParams();
  params.append('jwt', getToken()); // TODO: this can be removed once production is updated to use headers
  params.append('charity_id', getCharityId());
  params.append('title', title);
  params.append('body', body);
  params.append('type', type);
  params.append('url', url);

  let saveLink = '/data/impact';
  if (id) {
    saveLink += '/' + id;
  }

  return axios.post(apiUrl + saveLink, params, {
    headers: {
      'X-Harness-JWT': getToken(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

function deletePost(id) {
  return axios({
    method: 'delete',
    url: '/data/impact/' + id,
    baseURL: apiUrl,
    params: {
      jwt: getToken(),
    },
    headers: {
      'X-Harness-JWT': getToken(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

function logout() {
  auth.logOut();
}

function sendResetLink(email) {
  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('email', email);
    axios
      .post(apiUrl + '/client/password/reset', params)
      .then((res) => {
        resolve(res.data);
      })
      .catch((error) => {
        reject(error.response.data);
      });
  });
}

function getThread(threadId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/engage/${charityId}/threads/${threadId}`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

function getThreads(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/engage/${charityId}/threads`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Send a message to a single user
 * @param {int} userId
 * @param {string|object} message The content of the message. Either a string,
 *   or an object containing the field `body`, and optionally an `attachment`
 *   field as an array of URLs.
 * @param {string|boolean} charityId
 */
function sendMessage(userId, message, charityId = false) {
  // If the `message` is just a plain string, place it in a message object.
  if (typeof message !== 'object') {
    message = { body: message };
  }

  // Make `attachment` an empty array if it's not set.
  if (!message.attachment) {
    message.attachment = [];
  }

  // If an attachment is set, and it's a single element _not_ in an array,
  //   place it in an array.
  if (message.attachment && !Array.isArray(message.attachment)) {
    message.attachment = [message.attachment];
  }

  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('body', message.body);

    message.attachment.forEach((attachment) => {
      params.append('attachment[]', attachment);
    });

    axios
      .post(apiUrl + '/engage/' + charityId + '/threads/' + userId, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response);
      });
  });
}

/**
 *
 * @param {string|object} message The content of the message. Either a string,
 *   or an object containing the field ody, and optionally an ttachment
 *   field as an array of URLs.
 * @param {object} recipients An object containing arrays for `segments`,
 *   `lists`, `campaigns`, and `users` to which the message should be sent.
 *   Alternatively, supply pokedexSegments (array of Pokedex segment UUIDs)
 *   and/or pokedexContacts (array of Pokedex contact UUIDs) to send to those
 *   recipients via the HCA endpoint using pokedex_segment[]/pokedex_contact[] params.
 * @param {string|false} charityId
 */
function sendMessageBulk(message, recipients, charityId = false) {
  // If the `message` is just a plain string, place it in a message object.
  if (typeof message !== 'object') {
    message = { body: message };
  }

  // Make ttachment an empty array if it's not set.
  if (!message.attachment) {
    message.attachment = [];
  }

  // If an attachment is set, and it's a single element _not_ in an array,
  //   place it in an array.
  if (message.attachment && !Array.isArray(message.attachment)) {
    message.attachment = [message.attachment];
  }

  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('body', message.body);
    message.attachment.forEach((attachment) => {
      params.append('attachment[]', attachment);
    });

    if (recipients.pokedexSegments && Array.isArray(recipients.pokedexSegments)) {
      recipients.pokedexSegments.forEach((id) => {
        params.append('pokedex_segment[]', id);
      });
    }
    if (recipients.pokedexContacts && Array.isArray(recipients.pokedexContacts)) {
      recipients.pokedexContacts.forEach((id) => {
        params.append('pokedex_contact[]', id);
      });
    }
    if (recipients.segments && Array.isArray(recipients.segments)) {
      recipients.segments.forEach((segment) => {
        params.append('segment[]', segment);
      });
    }
    if (recipients.lists && Array.isArray(recipients.lists)) {
      recipients.lists.forEach((list) => {
        params.append('list[]', list);
      });
    }
    if (recipients.campaigns && Array.isArray(recipients.campaigns)) {
      recipients.campaigns.forEach((campaign) => {
        params.append('campaign[]', campaign);
      });
    }
    if (recipients.users && Array.isArray(recipients.users)) {
      recipients.users.forEach((user) => {
        params.append('user[]', user);
      });
    }

    axios
      .post(apiUrl + '/engage/' + charityId + '/message', params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        } else {
          reject(error);
        }
      });
  });
}

function getGetActiveMonthlyDonations(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/data/${charityId}/monthly`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

function getVideoUploadToken(charityId = false) {
  charityId = charityId ? charityId : getCharityId();
  return apiRequest.doPostRequest(
    `/data/${charityId}/upload/video/token`,
    {},
    { 'X-Harness-JWT': getToken() },
  );
}

function createDonor(userParams, charity_id) {
  charity_id = charity_id || getCharityId();

  if (userParams['phone_number'] && !userParams['country_code']) {
    userParams['country_code'] = 1; //1 default value
  }

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();

    for (let key in userParams) {
      if (userParams.hasOwnProperty(key) && userParams[key]) {
        params.append(key, userParams[key]);
      }
    }

    params.append('charity_id', charity_id);

    let endpoint = `${apiUrl}/data/contacts`;

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * POST /v2.0/data/donor/:uid
 * @param donorId
 * @param description
 */
function updateDonor(donorId, description) {
  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('description', description);

    let endpoint = `${apiUrl}/data/donor/${donorId}`;

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Delete a donor account, optionally marking it as spam/fraudulent.
 * @param {int} donorId The numeric ID of the donor account to delete
 * @param {string} charityId The Charity ID
 * @param {boolean} flagged TRUE to mark the donor as spam/fraudulent; FALSE otherwise.
 * @returns {Promise<any>}
 */
function deleteDonor(donorId, charityId, flagged = false) {
  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/charity/${charityId}/donors/${donorId}`;

    if (flagged) {
      endpoint += '?flagged=true';
    }

    axios
      .delete(endpoint, {
        headers: {
          'X-Harness-JWT': getToken(),
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

function getDonor(donorId, charityId = false, skipCache = true) {
  return apiRequest.doGetRequest(
    `/data/donor/${donorId}`,
    {
      charity_id: charityId ? charityId : getCharityId(),
    },
    { 'X-Harness-JWT': getToken() },
    { skipCache }
  )
}

function getDonorDonations(donorId, charityId = false) {
  return apiRequest.doGetRequest(
    `/data/donor/${donorId}/donations`,
    {
      jwt: getToken(),
      charity_id: charityId ? charityId : getCharityId(),
    },
    {},
    { skipCache: true },
  );
}

function getActivity(charityId = false) {
  return apiRequest.doGetRequest('/data/donor/activity', {
    jwt: getToken(),
    charity_id: charityId ? charityId : getCharityId(),
  });
}

function getOnePost(postId, charityId = false) {
  return new Promise((resolve) => {
    charityId = charityId ? charityId : getCharityId();
    getPosts(charityId).then((posts) => {
      let post = false;

      for (let i = 0; i < posts.length; i++) {
        if (posts[i].feedback_id == postId) {
          post = posts[i];
        }
      }

      resolve(post);
    });
  });
}

/**
 * GET View single Impact Post
 * @param postId
 * @returns {Promise}
 */
function getPost(postId) {
  return apiRequest.doGetRequest(
    `/impact/${postId}`,
    {},
    {},
    { skipCache: true },
  );
}

/**
 * get all impact posts
 * @param charityId
 * @returns {Promise}
 */
function getPosts(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    '/data/impact',
    {
      jwt: getToken(),
      charity_id: charityId,
    },
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Get all folders
 * @param charityId
 * @returns {Promise}
 */
function getFolders(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/engage/${charityId}/folders`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * GET Get Folder
 * @param folderId
 * @param charityId
 * @returns {Promise}
 */
function getFolder(folderId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/engage/${charityId}/folders/${folderId}`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Get Template
 * @param templateId
 * @param charityId
 * @returns {Promise}
 */
function getTemplate(templateId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/engage/${charityId}/templates/${templateId}`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Get All Templates
 * @param charityId
 * @returns {Promise}
 */
function getAllTemplates(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/engage/${charityId}/templates`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * POST Create new Folder
 * {{url}}/engage/:charityId/folders
 */
function createFolder(name, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('name', name);

    let endpoint = `${apiUrl}/engage/${charityId}/folders`;

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        reject(error.response.data);
      });
  });
}

/**
 * POST Create new Template
 * {{url}}/engage/:charityId/templates
 */
function createTemplate(
  name,
  body,
  folder_id,
  attachment = false,
  charityId = false,
) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('name', name);
    params.append('body', body);
    params.append('folder_id', folder_id);
    if (attachment) {
      params.append('attachment', attachment);
    }

    let endpoint = `${apiUrl}/engage/${charityId}/templates`;

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Edit a Template
 * {{url}}/engage/:charityId/templates/:templateId
 */
function editTemplate(
  name,
  body,
  templateId,
  attachment = false,
  folderId = false,
  charityId = false,
) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('name', name);
    params.append('body', body);
    if (attachment) {
      params.append('attachment', attachment);
    }
    if (folderId) {
      params.append('folder_id', folderId);
    }

    let endpoint = `${apiUrl}/engage/${charityId}/templates/${templateId}`;

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Delete a Template
 * {{url}}/engage/:charityId/templates/:templateId
 */
function deleteTemplate(templateId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/engage/${charityId}/templates/${templateId}`;

    axios
      .delete(endpoint, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Get all campaigns
 * {{url}}/charity/:charityId/campaigns
 */
function getAllCampaigns(includeArchived = false, charityId = false) {
  charityId = charityId || getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/campaigns`,
    includeArchived ? { include_archived: 1 } : {},
    includeArchived ? { 'X-Harness-JWT': getToken() } : {},
    { returnParam: 'campaigns', skipCache: true },
  );
}

/**
 * POST Create a new Campaign
 * {{url}}/charity/:charityId/campaigns
 *
 * @param {string} name
 * @param {string} description
 * @param {string} image A URL for the image associated with the new campaign
 * @param goal (Optional) An integer, in cents USD, representing the funding goal for the campaign.
 * @param endsAt
 * @param showSettings
 * @param default_flow_id
 * @param charityId
 * @returns {Promise}
 */
function createCampaign(
  name,
  description,
  image,
  goal,
  endsAt,
  showSettings = {},
  default_flow_id = false,
  charityId = false,
) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('name', name);
    params.append('description', description);
    params.append('image', image);
    params.append('goal', goal);
    if (default_flow_id !== false) {
      params.append('default_flow_id', default_flow_id);
    }
    if (endsAt) {
      params.append(
        'end',
        moment(endsAt)
          .milliseconds(0)
          .toISOString()
          .split('.')[0] + 'Z',
      );
    }

    Object.entries(showSettings).map(([key, val]) => {
      if (showSettings.hasOwnProperty(key)) {
        params.append(
          key
            .split(/(?=[A-Z])/)
            .join('_')
            .toLowerCase(),
          val,
        );
      }
    });

    let endpoint = `${apiUrl}/charity/${charityId}/campaigns`;

    axios
      .post(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * PUT Edit a campaign
 * {{url}}/charity/:charityId/campaigns/:campaignId
 *
 * @param {number} campaignId
 * @param {string} name
 * @param {string} description
 * @param {string} image
 * @param goal
 * @param unarchive
 * @param endsAt
 * @param showSettings
 * @param default_flow_id
 * @param charityId
 * @returns {Promise}
 */
function editCampaign(
  campaignId,
  name,
  description,
  image,
  goal,
  unarchive = false,
  endsAt = false,
  showSettings = {},
  default_flow_id = false,
  charityId = false,
) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('name', name);
    params.append('description', description);
    params.append('image', image);
    params.append('goal', goal);
    if (default_flow_id !== false) {
      params.append('default_flow_id', default_flow_id);
    }

    if (unarchive) {
      params.append('unarchive', 1);
    }

    if (endsAt) {
      params.append(
        'end',
        moment(endsAt)
          .milliseconds(0)
          .toISOString()
          .split('.')[0] + 'Z',
      );
    } else {
      params.append('end', '');
    }

    Object.entries(showSettings).map(([key, val]) => {
      if (showSettings.hasOwnProperty(key)) {
        params.append(
          key
            .split(/(?=[A-Z])/)
            .join('_')
            .toLowerCase(),
          val,
        );
      }
    });

    let endpoint = `${apiUrl}/charity/${charityId}/campaigns/${campaignId}`;

    axios
      .put(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Get All Categories
 * @param charityId
 * @returns {Promise}
 */
function getAllCategories(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/categories`,
    {},
    {},
    { skipCache: true, returnParam: 'categories' },
  );
}

function editCategory(categoryId, paramsObject, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();

    for (let key in paramsObject) {
      if (paramsObject.hasOwnProperty(key)) {
        if (Array.isArray(paramsObject[key])) {
          paramsObject[key].map((oneParam) => {
            params.append(`${key}[]`, oneParam);
          });
        } else {
          params.append(key, paramsObject[key]);
        }
      }
    }

    let endpoint = `${apiUrl}/charity/${charityId}/categories/${categoryId}`;

    axios
      .put(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * POST Create a Category
 * POST {{url}}/charity/:charityId/categories
 */
function createCategory(name, description, image = false, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('name', name);
    params.append('description', description);
    if (image) {
      params.append('image', image);
    }

    axios
      .post(`${apiUrl}/charity/${charityId}/categories`, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Delete a Category
 * {{url}}/charity/:charityId/categories/:categoryId
 *
 * @param categoryId
 * @param charityId
 * @returns {Promise}
 */
function deleteCategory(categoryId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    axios
      .delete(`${apiUrl}/charity/${charityId}/categories/${categoryId}`, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => resolve(res.data))
      .catch((error) => {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * set or update or remove a category for a campaign
 * @param campaignId
 * @param categoryId
 */
function setCampaignCategory(campaignId, categoryId = 0) {
  return new Promise((resolve) => {
    categoryId = parseInt(categoryId);
    campaignId = parseInt(campaignId);

    getAllCategories().then((categoies) => {
      let allCalls = [];
      categoies.map((category) => {
        let cmpIds = category.campaigns.map((cmp) => parseInt(cmp.id));

        if (categoryId && parseInt(category.id) === categoryId) {
          if (cmpIds.indexOf(campaignId) === -1) {
            allCalls.push(
              editCategory(category.id, {
                campaign: [...cmpIds, campaignId],
                description: category.description,
                name: category.name,
                image: category.image,
              }),
            );
          }
        } else if (cmpIds.indexOf(campaignId) !== -1) {
          allCalls.push(
            editCategory(category.id, {
              campaign: cmpIds.filter((tmpId) => tmpId !== campaignId),
              description: category.description,
              name: category.name,
              image: category.image,
            }),
          );
        }
      });

      if (allCalls.length) {
        Promise.all(allCalls).then((res) => resolve(res));
      } else {
        resolve(null);
      }
    });
  });
}

/**
 * Delete a Campaign
 * {{url}}/charity/:charityId/campaigns/:campaignId
 *
 * @param campaignId
 * @param charityId
 * @returns {Promise}
 */
function deleteCampaign(campaignId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    axios
      .delete(`${apiUrl}/charity/${charityId}/campaigns/${campaignId}`, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => resolve(res.data))
      .catch((error) => {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * GET Recent Payouts
 * @param charityId
 * @returns {Promise}
 */
function getRecentPayouts(charityId = false) {
  charityId = charityId || getCharityId();

  if (charityId === '5eafa414209b233147eb') {
    return new Promise((resolve) => {
      resolve([
        {
          id: 'po_1FplfeGYAcvrDu0xwTbokD0E',
          amount_cents: 144101,
          amount_currency: 'usd',
          destination: 'FIRST CITIZENS BANK & TRUST COMPANY',
          date_paid: 'Dec 15',
        },
        {
          id: 'po_1FogMrGYAcvrDu0xBY4RagTq',
          amount_cents: 120650,
          amount_currency: 'usd',
          destination: 'FIRST CITIZENS BANK & TRUST COMPANY',
          date_paid: 'Dec 12',
        },
        {
          id: 'po_1FnxBIGYAcvrDu0xyEpETTBE',
          amount_cents: 69414,
          amount_currency: 'usd',
          destination: 'FIRST CITIZENS BANK & TRUST COMPANY',
          date_paid: 'Dec 10',
        },
      ]);
    });
  }

  return apiRequest.doGetRequest(
    `/data/payouts`,
    { charity_id: charityId },
    { 'X-Harness-JWT': getToken() },
  );
}

/**
 * GET Transactions part of Payout
 * @param payoutid
 * @param charityId
 * @returns {Promise}
 */
function getPayoutTransactions(payoutid, charityId = false) {
  charityId = charityId || getCharityId();

  if (charityId === '5eafa414209b233147eb') {
    return new Promise((resolve) => {
      resolve([
        {
          stripe_source_id: 'ch_1HStH7JDBff1.....',
          available_on: 1600732800,
          created_at: 1600472757,
          gross_amount: 1061,
          net_amount: 1008,
          stripe_fee: 53,
          platform_fee: 0,
          currency: 'usd',
          description: 'Invoice 1122233344-555',
          payment_source: {
            type: 'card',
            exp_month: 9,
            exp_year: 2031,
            last4: '4444',
            brand: 'visa',
            cvc_check: null,
          },
          metadata: {
            type: 'recurring',
            harness_id: 'e9d91efb-15fa-445e-1111-22222222',
            charity_id: '5eafa414209b233147eb',
            charity_name: 'Test Charity',
            first_name: 'first_name',
            last_name: 'last_name',
            donor_email: 'test@example.com',
            to_charity: '5eafa414209b233147eb',
            harness_platform: 'true',
            fee_paid_by_donor: '$0.61',
          },
        },
        {
          stripe_source_id: 'ch_1HStH7JDBff1.....',
          available_on: 1600732800,
          created_at: 1600472757,
          gross_amount: 1061,
          net_amount: 1008,
          stripe_fee: 53,
          platform_fee: 0,
          currency: 'usd',
          description: 'Invoice 1122233344-555',
          payment_source: {
            type: 'card',
            exp_month: 9,
            exp_year: 2031,
            last4: '4444',
            brand: 'visa',
            cvc_check: null,
          },
          metadata: {
            type: 'recurring',
            harness_id: 'e9d91efb-15fa-445e-1111-22222222',
            charity_id: '5eafa414209b233147eb',
            charity_name: 'Test Charity',
            first_name: 'name',
            last_name: 'lname',
            donor_email: 'test222@example.com',
            to_charity: '5eafa414209b233147eb',
            harness_platform: 'true',
            fee_paid_by_donor: '$0.61',
          },
        },
      ]);
    });
  }

  return apiRequest.doGetRequest(
    `/data/payouts/${payoutid}`,
    { charity_id: charityId },
    { 'X-Harness-JWT': getToken() },
  );
}

/**
 * GET Get Upload URI and Token
 * @returns {Promise}
 */
function getCharityUploadUri(charity_id) {
  charity_id = charity_id ? charity_id : getCharityId();
  return apiRequest.doPostRequest(
    `/charity/${charity_id}/image/upload`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * GET Get Upload URI and Token
 * @returns {Promise}
 */
function globalImageUploader(file, onUploadProgress = null, charity_id) {
  return new Promise((resolve, reject) => {
    getCharityUploadUri(charity_id)
      .then((uploadInfo) => {
        uploadImage(uploadInfo.uploadUrl, file, onUploadProgress)
          .then((response) => {
            resolve(response.data.result.variants[0]);
          })
          .catch((e) => {
            if (e.response && e.response.status === 415) {
              // eslint-disable-next-line
              reject(
                'Unsupported media type: the file must be a jpeg, png, webp, gif, or svg image file.',
              );
            } else {
              reject(e);
            }
          });
      })
      .catch(() => reject());
  });
}
/**
 * GET Get Upload URI and Token
 * @returns {Promise}
 */
function getUploadUriAndToken() {
  return apiRequest.doPostRequest(
    `/client/profile-photo/image/upload`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Get Profile Settings
 * @param charityId
 * @returns {Promise}
 */
function getProfileSettings(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/settings/profile`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * PUT Update Profile Settings
 * {{url}}/charity/:charityId/settings/profile
 *
 * @param params
 * @param charityId
 * @returns {Promise}
 */
function putProfileSettings(params, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doPutRequest(
    `/charity/${charityId}/settings/profile`,
    params,
    {'content-type': 'application/json'},
  );
}

/**
 * Get Organization Settings
 * @param charityId
 * @returns {Promise}
 */
function getOrganizationSettings(charityId = false) {
  charityId = charityId ? charityId : getCharityId();
  return apiRequest.doGetRequest(
    `/charity/${charityId}/settings/organization`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * PUT Update Organization Settings
 * {{url}}/charity/:charityId/settings/organization
 *
 * @param params
 * @param charityId
 * @returns {Promise}
 */
function putOrganizationSettings(params, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doPutRequest(
    `/charity/${charityId}/settings/organization`,
    params,
    {'content-type': 'application/json'},
  );
}

/**
 * Get Teammates
 *
 * @param charityId
 * @returns {Promise}
 */
function getTeammates(charityId) {
  return apiRequest.doGetRequest(
    `/charity/${charityId}/settings/teammates`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Remove a Teammate
 *
 * @param cuid - teammate.id
 * @param charityId
 * @returns {Promise}
 */
function removeTeammate(cuid, charityId) {
  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/charity/${charityId}/settings/teammates/${cuid}`;

    axios
      .delete(endpoint, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Get Organization Billing Settings
 *
 * @param charityId
 * @returns {Promise}
 */
function getOrganizationBillingSettings(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/billing/card`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Get Organization Billing Plan
 *
 * @param charityId
 * @returns {Promise}
 */
function getOrganizationBillingPlan(charityId = false) {
  charityId = charityId ? charityId : getCharityId();
  let params = new URLSearchParams();
  params.append('jwt', getToken());
  return apiRequest.doGetRequest(`/charity/${charityId}/billing/plan`, params);
}

/**
 * POST Update Organization Billing Card
 * {{url}}/charity/:charityId/billing/card
 */
function updateOrganizationBillingCard(stripe_token, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('stripe_token', stripe_token);

    return axios
      .post(`${apiUrl}/charity/${charityId}/billing/card`, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * POST Confirm photo uploaded to S3
 * {{url}}/account/profile-photo/upload
 *
 * @param imageId
 * @returns {Promise}
 */
function confirmPhotoUploaded(imageId) {
  return apiRequest.doPostRequest(
    `/client/profile-photo/image/upload/${imageId}`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Get status of photo upload
 *
 * @returns {Promise}
 */
function getPhotoStatus() {
  return apiRequest.doGetRequest(
    `/client/profile-photo/status`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * Upload Image to specified URL
 * @param uri
 * @param file
 * @returns {Promise}
 */
function uploadImage(uri, file, onUploadProgress = false) {
  let formData = new FormData();
  formData.append('file', file);

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };

  if (onUploadProgress) {
    config['onUploadProgress'] = onUploadProgress;
  }

  return new Promise((resolve, reject) => {
    axios
      .post(uri, formData, config)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * GET Get available merge tags
 * @param auto_message_id
 * @param charityId
 * @returns {Promise}
 */
function getAvailableMergeTags(auto_message_id, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  let params = {};
  if (auto_message_id) {
    params['auto_message_id'] = auto_message_id;
  }

  return apiRequest.doGetRequest(`/engage/${charityId}/message/tags`, params, {
    'X-Harness-JWT': getToken(),
  });
}

/**
 * Get Merge Tag message previews
 *
 * @param body
 * @param auto_message_id
 * @param seed
 * @param charityId
 * @returns {Promise}
 */
function getMergeTagPreview(
  body,
  auto_message_id = false,
  seed = false,
  charityId = false,
) {
  charityId = charityId ? charityId : getCharityId();

  let params = {
    body,
  };
  if (auto_message_id) {
    params['auto_message_id'] = auto_message_id;
  }
  if (seed) {
    params['seed'] = seed;
  }

  return apiRequest.doGetRequest(
    `/engage/${charityId}/message/preview`,
    params,
    { 'X-Harness-JWT': getToken() },
  );
}

/**
 * Get all Auto Messages
 *
 * @param charityId
 * @returns {Promise}
 */
function getAllAutoMessages(charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return apiRequest.doGetRequest(
    `/charity/${charityId}/auto-messages`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * PUT Update an Auto Message
 *
 * @param messageId
 * @param body
 * @param enabled
 * @param attachment
 * @param charityId
 * @returns {Promise}
 */
function updateMessage(
  messageId,
  body,
  enabled,
  attachment = false,
  charityId = false,
) {
  charityId = charityId ? charityId : getCharityId();
  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('body', body);
    params.append('enabled', enabled);
    if (attachment) {
      params.append('attachment', attachment);
    } else {
      params.append('attachment', '');
    }

    let endpoint = `${apiUrl}/charity/${charityId}/auto-messages/${messageId}`;

    axios
      .put(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * GET Current Keyword
 * @param charityId
 * @returns {Promise}
 */
function getCurrentKeyword(charityId) {
  return apiRequest.doGetRequest(
    `/charity/${charityId}/text-in-keyword?v2=true`,
    {},
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * GET Check Keyword Availability
 * @param keyword
 * @param charityId
 * @returns {Promise}
 */
function checkKeywordAvailability(keyword, charityId) {
  return apiRequest.doGetRequest(
    `/charity/${charityId}/text-in-keyword/availability`,
    { keyword },
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
  );
}

/**
 * PUT Register/Edit a Keyword
 * GET /v2.0/charity/:charityId/text-in-keyword
 */
function registerKeyword(keyword, message, charityId) {
  return new Promise((resolve, reject) => {
    let params = new URLSearchParams();
    params.append('keyword', keyword);
    params.append('message', message);

    let endpoint = `${apiUrl}/charity/${charityId}/text-in-keyword?v2=true`;

    axios
      .put(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * Delete a Keyword
 * {{url}}/charity/:charityId/text-in-keyword
 */
function deleteKeyword(templateId, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/charity/${charityId}/text-in-keyword/${templateId}`;

    axios
      .delete(endpoint, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * PATCH Enable/Disable Donor Phone Number
 * {{url}}/charity/:charityId/donors/:donorId/phone
 *
 * @param donorId
 * @param enabled
 * @param charityId
 * @returns {Promise}
 */
function patchDonorPhone(donorId, enabled = 0, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/charity/${charityId}/donors/${donorId}/phone`;

    let params = new URLSearchParams();
    params.append('enabled', enabled);

    axios
      .patch(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * PATCH Edit a contact's address.
 * {{url}}/data/donor/:uid/address
 *
 * @param donorId
 * @param parameters
 * @param charityId
 * @returns {Promise}
 */
function patchDonorAddress(donorId, parameters, charityId = false) {
  charityId = charityId ? charityId : getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/data/donor/${donorId}/address`;

    let params = new URLSearchParams();
    params.append('charity_id', charityId);

    for (let key in parameters) {
      if (parameters.hasOwnProperty(key)) {
        params.append(key, parameters[key]);
      }
    }

    axios
      .patch(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * PATCH Edit a contact's employment info
 * {{url}}/data/donor/:uid/employment
 *
 * @param donorId
 * @param parameters
 * @param charityId
 * @returns {Promise}
 */
function patchDonorEmployment(donorId, parameters, charityId = false) {
  charityId = charityId || getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/data/donor/${donorId}/employment`;

    let params = new URLSearchParams();
    params.append('charity_id', charityId);

    for (let key in parameters) {
      if (parameters.hasOwnProperty(key)) {
        params.append(key, parameters[key]);
      }
    }

    axios
      .patch(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * PATCH Edit a contact's phone number
 * {{url}}/data/donor/:uid/phone
 *
 * @param donorId
 * @param parameters
 * @param charityId
 * @returns {Promise}
 */
function patchDonorPhoneNumber(donorId, parameters, charityId = false) {
  charityId = charityId || getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/data/donor/${donorId}/phone`;

    let params = new URLSearchParams();

    if (parameters['phone_number'] && !parameters['country_code']) {
      parameters['country_code'] = 1; //1 default value
    }
    //curl 'http://api.giphy.com/v1/gifs/trending?limit=50&api_key=F0xXH4MlIP1fjMo4bWHtk2CmJFqpYi7G' -H 'Referer: https://admin.harnessapp.com/' -H 'Origin: https://admin.harnessapp.com' --compressed
    params.append('charity_id', charityId);

    for (let key in parameters) {
      if (parameters.hasOwnProperty(key)) {
        params.append(key, parameters[key]);
      }
    }

    axios
      .patch(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

/**
 * PATCH Edit a contact's personal info
 * {{url}}/data/donor/:uid/details
 *
 * @param donorId
 * @param parameters
 * @param charityId
 * @returns {Promise}
 */
function patchDonorPersonalInfo(donorId, parameters, charityId = false) {
  charityId = charityId || getCharityId();

  return new Promise((resolve, reject) => {
    let endpoint = `${apiUrl}/data/donor/${donorId}/details`;

    let params = new URLSearchParams();
    params.append('charity_id', charityId);

    for (let key in parameters) {
      if (parameters.hasOwnProperty(key)) {
        params.append(key, parameters[key]);
      }
    }

    axios
      .patch(endpoint, params, {
        headers: {
          'X-Harness-JWT': getToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch(function(error) {
        if (error.response.data.error === 'Invalid JWT') {
          logout();
        }
        reject(error.response.data);
      });
  });
}

const donationFlow = {
  getAll(charityId) {
    return apiRequest.doGetRequest(
      `/charity/${charityId}/flows`,
      {},
      { 'X-Harness-JWT': getToken() },
      { returnParam: 'flows', skipCache: true },
    );
  },
  getOne(flowId, charityId) {
    return apiRequest.doGetRequest(
      `/donate/${charityId}/flows/${flowId}`,
      {},
      {},
      { returnParam: 'flow' },
    );
  },
  update(flowId, params, charityId) {
    return apiRequest.doPutRequest(
      `/charity/${charityId}/flows/${flowId}`,
      params,
      {'content-type': 'application/json'},
    );
  },
  async create(params, charityId) {
    return await apiRequest.doPostRequest(
      `/charity/${charityId}/flows`,
      params,
      {'content-type': 'application/json'},
    );
  },
  archive(flowId, charityId) {
    return apiRequest.doPutRequest(
      `/charity/${charityId}/flows/${flowId}/archive`,
    );
  },
  unarchive(flowId, charityId) {
    return apiRequest.doDeleteRequest(
      `/charity/${charityId}/flows/${flowId}/archive`,
    );
  },
  delete(flowId, charityId) {
    return apiRequest.doDeleteRequest(`/charity/${charityId}/flows/${flowId}`);
  },
  uploadImage(charityId) {
    return apiRequest.doGetRequest(
      `/charity/${charityId}/flows/upload`,
      {},
      { 'X-Harness-JWT': getToken() },
    );
  },
};

const customFields = {
  getAll(charityId) {
    return apiRequest.doGetRequest(
      `/charity/${charityId}/custom-fields`,
      {},
      { 'X-Harness-JWT': getToken() },
      { returnParam: 'fields', skipCache: true },
    );
  },
  getOne(fieldId, charityId) {
    return apiRequest.doGetRequest(
      `/charity/${charityId}/custom-fields/${fieldId}`,
    );
  },
  create(params, charityId) {
    return apiRequest.doPostRequest(
      `/charity/${charityId}/custom-fields`,
      params,
      {'content-type': 'application/json'},
    );
  },
  update(fieldId, params, charityId) {
    return apiRequest.doPutRequest(
      `/charity/${charityId}/custom-fields/${fieldId}`,
      params,
      {'content-type': 'application/json'},
    );
  },
  archive(fieldId, charityId) {
    return apiRequest.doPutRequest(
      `/charity/${charityId}/custom-fields/${fieldId}/archive`,
    );
  },
  unarchive(fieldId, charityId) {
    return apiRequest.doDeleteRequest(
      `/charity/${charityId}/custom-fields/${fieldId}/archive`,
    );
  },
  delete(fieldId, charityId) {
    return apiRequest.doDeleteRequest(
      `/charity/${charityId}/custom-fields/${fieldId}`,
    );
  },
};

const notificationSettings = {
  /**
   * Get Notification Settings
   * @param charityId
   * @returns {Promise}
   */
  getNotificationSettings(charityId) {
    return apiRequest.doGetRequest(
      `/charity/${charityId}/settings/notifications`,
      {},
      { 'X-Harness-JWT': getToken() },
      { skipCache: true },
    );
  },
  /**
   * POST Enable/Disable a Notification
   * @param notificationId
   * @param enabled
   * @param charityId
   * @returns {Promise}
   */
  updateNotificationSetting(notificationId, enabled, charityId) {
    return apiRequest.doPostRequest(
      `/charity/${charityId}/settings/notifications/${notificationId}?enabled=${enabled}`,
    );
  },
};

const ImpactAmounts = {
  /**
   * Get Donation Impact Amounts
   * @param charityId
   * @returns {Promise}
   */
  getImpactAmounts(charityId) {
    return apiRequest.doGetRequest(
      `/charity/${charityId}/donation-impacts`,
      {},
      {},
      { skipCache: true, returnParam: 'impacts' },
    );
  },
  create(name, description, amount, image, charityId) {
    let params = new URLSearchParams();
    params.append('name', name);
    params.append('description', description);
    if (image) {
      params.append('image', image);
    }
    params.append('amount', amount);

    return apiRequest.doPostRequest(
      `/charity/${charityId}/donation-impacts`,
      params,
    );
  },
  update(impactId, name, description, amount, image, charityId) {
    let params = new URLSearchParams();
    params.append('name', name);
    params.append('description', description);
    if (image) {
      params.append('image', image);
    }
    params.append('amount', amount);

    return apiRequest.doPutRequest(
      `/charity/${charityId}/donation-impacts/${impactId}`,
      params,
    );
  },
  delete(impactId, charityId) {
    return apiRequest.doDeleteRequest(
      `/charity/${charityId}/donation-impacts/${impactId}`,
    );
  },
};

const EventsTickets = {
  async getStoreRequestHeaders(charityId) {
    return await $store.dispatch("events/getStoreRequestHeaders", {
      charityId,
      clientJwt: getToken(),
    });
  },

  async eventStoreLogin(donorId, storeId) {
    try {
      // Generate proxy token
      const { proxy } = await $store.dispatch("donors/createProxyToken", donorId);
      if (!proxy?.token) {
        throw new Error('Failed to generate proxy token');
      }

      // Authenticate with event service
      const loginResponse = await apiRequest.doPostRequest(
        '/event-service/customer/login',
        JSON.stringify({ jwt: proxy.token }),
        {
          'X-Harness-Store-Id': storeId,
          'Content-Type': 'application/json'
        },
        { skipJWT: true }
      );

      return loginResponse.data;

    } catch (error) {
      captureException(error);
      throw error;
    }
  },

  async checkoutCart({ token, storeId, cartId, params }) {
    try {
      // Process checkout
      const response = await apiRequest.doPostRequest(
        `/event-service/checkout/${cartId}`,
        JSON.stringify(params),
        {
          'X-Harness-Store-Id': storeId,
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        { skipJWT: true },
      );

      return response.data;

    } catch (error) {
      captureException( error);
      throw error;
    }
  },

  async getEventsList(charityId) {
    charityId = charityId || getCharityId();

    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doGetRequest(
      `/event-service/admin/events`,
      {},
      storeHeaders,
      { returnParam: "data" }
    );
  },
  async getEventDetails(charityId, eventId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doGetRequest(
      `/event-service/admin/event/${eventId}`,
      {},
      storeHeaders,
      { skipCache: true, returnParam: "data" }
    );
  },
  async getEventTickets(charityId, eventId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doGetRequest(
      `/event-service/admin/event/${eventId}/tickets`,
      {},
      storeHeaders,
      { skipCache: true, returnParam: "data" }
    );
  },
  async getTicketsList(charityId, eventId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doGetRequest(
      `/event-service/admin/event/${eventId}/transactions`,
      {},
      storeHeaders,
      { skipCache: true, returnParam: "data" }
    );
  },
  async getAllTransactionsList(charityId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doGetRequest(
      `/event-service/admin/transactions`,
      {},
      storeHeaders,
      { skipCache: true, returnParam: "data" }
    );
  },
  async addEvent(params, charityId) {
    if (!params['isVirtual']) {
      if (params['address']) {
        const address = await this.addAddress(params['address'], charityId);
        params['addressId'] = address['data']['address']['id'];
        delete params['address'];
      }
    }
    params['timezone'] = params['timezone'] ? params['timezone'] : 'UTC';

    if (params['featuredImage']) {
      const image = await this.uploadImage(params['featuredImage'], charityId);
      params['featuredImageId'] = image.data;
      delete params['featuredImage'];
    }

    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doPostRequest(`/event-service/admin/event`, params, {
      "Content-Type": "application/json",
      ...storeHeaders,
    });
  },
  async updateEvent(eventId, eventDetails, charityId) {
    eventDetails['addressId'] = null;
    if (!eventDetails['isVirtual'] && eventDetails['address']) {
      if (!eventDetails['address']['id']) {
        const address = await this.addAddress(
          eventDetails['address'],
          charityId,
        );
        eventDetails['addressId'] = address['data']['address']['id'];
        delete eventDetails['address'];
      } else {
        eventDetails['addressId'] = eventDetails['address']['id'];
        delete eventDetails['address'];
      }
    }

    if (eventDetails['featuredImage']) {
      const image = await this.uploadImage(
        eventDetails['featuredImage'],
        charityId,
      );
      eventDetails['featuredImageId'] = image.data;
      delete eventDetails['featuredImage'];
    }

    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doPutRequest(
      `/event-service/admin/event/${eventId}`,
      eventDetails,
      {
        "Content-Type": "application/json",
        ...storeHeaders,
      }
    );
  },
  async addAddress(address, charityId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doPostRequest(`/event-service/admin/address`, address, {
      "Content-Type": "application/json",
      ...storeHeaders,
    });
  },
  async createTicket(ticketDetails, charityId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doPostRequest(
      `/event-service/admin/event/${ticketDetails.eventId}/ticket`,
      ticketDetails,
      {
        "Content-Type": "application/json",
        ...storeHeaders,
      }
    );
  },
  async updateTicket(ticketDetails, charityId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);
    const ticketId = ticketDetails.id;
    delete ticketDetails.id;

    return apiRequest.doPutRequest(
      `/event-service/admin/event/${ticketDetails.eventId}/ticket/${ticketId}`,
      ticketDetails,
      {
        "Content-Type": "application/json",
        ...storeHeaders,
      }
    );
  },
  async deleteTicket(ticketDetails, charityId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    return apiRequest.doDeleteRequest(
      `/event-service/admin/event/${ticketDetails.eventId}/ticket/${ticketDetails.id}`,
      {
        "Content-Type": "application/json",
        ...storeHeaders,
      }
    );
  },
  async updateEventTicketOrder(eventId, sortedTicketIds, charityId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    let eventDetails = {
      order: {
        products: sortedTicketIds,
      },
    };

    return apiRequest.doPutRequest(
      `/event-service/admin/event/${eventId}/tickets/order`,
      eventDetails,
      {
        "Content-Type": "application/json",
        ...storeHeaders,
      }
    );
  },
  async uploadImage(image, charityId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);

    const imageToUpload = {
      mimeType: image.mimeType,
      url: image.url,
      thumbnail: image.thumbnail,
    };

    return apiRequest.doPostRequest(
      `/event-service/admin/asset`,
      imageToUpload,
      {
        "Content-Type": "application/json",
        ...storeHeaders,
      }
    );
  },
  async updateQuantity(quantity, ticketDetails, charityId) {
    const storeHeaders = await this.getStoreRequestHeaders(charityId);
    let sufix = "";
    if (quantity < 0) {
      sufix = 'remove';
      quantity = quantity * -1;
    } else {
      sufix = 'add';
    }

    return apiRequest.doPostRequest(
      `/event-service/admin/product/${ticketDetails.id}/stock/${sufix}`,
      { quantityChange: quantity },
      {
        "Content-Type": "application/json",
        ...storeHeaders,
      }
    );
  },
};

const ACHOfflineDonation = {
  /**
   *
   * @param {string} harnessId
   * @param {string|null} charityId Charity ID
   * @returns {Promise}
   */
  async createSetupIntent(harnessId, charityId = null) {
    charityId = charityId || getCharityId();
    const formData = new FormData()
    formData.append('harness_id', harnessId)
    return axios.post(
      `${apiUrl}/account/funding/${charityId}/setup`,
      {},
      {
        headers: {
          'X-Harness-JWT': getToken(),
          'X-HARNESS-CLIENT-JWT': getToken(),
          'X-HARNESS-DONOR-ID': harnessId
        }
      },
    );
  }
}

/**
 * Get Donor cards through a client
 * @param donorId Donor id
 * @returns {Promise}
 */
function getClientDonorCards(donorId) {
  return apiRequest.doGetRequest(
    `/card`,
    {},
    { 'X-HARNESS-CLIENT-JWT': getToken(), 'X-HARNESS-DONOR-ID': donorId },
    { skipCache: true },
  );
}
/**
 * Get Donor funding sources through a client
 * @returns {Promise}
 */
function fetchAllFundingSources(donorId) {
  return apiRequest.doGetRequest(
    `/account/funding`,
    {
      jwt: getToken(),
    },
    { "X-HARNESS-CLIENT-JWT": getToken(), "X-HARNESS-DONOR-ID": donorId },
    { skipCache: true }
  );
}

/**
 * Get donation fee
 * @param amount Amount to donate
 * @param charityId Selected charity ID
 * @param type Must be one of one-time, recurring or round-up
 * @param cancelToken Axios cancel token
 * @returns {Promise}
 */
function getDonationFee(amount, charityId, type, cancelToken, paymentMethod = 'card') {
  const requestParams = {
    amount,
    charity_id: charityId,
    type,
    payment_method: paymentMethod,
  };

  return apiRequest.doGetRequest(
    `/donate/fee`,
    requestParams,
    { 'X-Harness-JWT': getToken() },
    { skipCache: true },
    cancelToken,
  );
}

async function createNewCart(charityId) {
  const storeIdResponse = await apiRequest.doGetRequest(
    `/event-service/store/charity/${charityId}`,
  );
    const storeID = storeIdResponse.data.id;
    const cartResponse = await axios.get(
      `${apiUrl}/event-service/cart`,
      {
        headers: {
          'X-Harness-Store-ID': storeID
        }
      }
    );
    return { cartId: cartResponse.data.data.cart.id, storeId: storeID };
  }

const getCart = async (storeID, cartId, includeFees = true) => {
  const response = await axios.get(
    `${apiUrl}/event-service/cart/${cartId}?includeFees=${includeFees}`,
    {
      headers: {
        'X-Harness-Store-ID': storeID
      }
    })
      return response.data.data.cart;
}

async function addTicketToCart(storeID, cartId, ticketId, quantity) {
  await axios.post(
    `${apiUrl}/event-service/cart/${cartId}`,
    {
      productId: ticketId,
      quantity: quantity
    },
    {
      headers: {
        'X-HARNESS-STORE-ID': storeID
      }
    }).catch(error => {
      captureException(error);
      throw error;
    });
}

const removeTicketFromCart = (storeID, cartId, ticketId) => {
  axios
      .delete(`${apiUrl}/event-service/cart/${cartId}/product/${ticketId}`, {
        headers: {
          'X-Harness-Store-ID': storeID,
        },
      }).catch(error => {
        captureException(error);
        throw error;
      });
}

const addContactToCart = async (storeID, cartId, itemId, contactInformation) => {
  const phoneToE164 = (number) => {
    return number.replace(/[^a-zA-Z0-9+]/g, '');
  };
  return axios
      .put(
        `${apiUrl}/event-service/cart/${cartId}/item/${itemId}`,
        { "contactInformation": {
          firstName: contactInformation.firstName,
          lastName: contactInformation.lastName,
          email: contactInformation.email,
          phoneNumber: phoneToE164(contactInformation.phoneNumber),
         } },
        {
        headers: {
          'X-Harness-Store-ID': storeID
        }
      })
      .catch(error => {
          throw error;
      });
}

/**
 * Get donors
 * @param {Object} donorParams object parameters
 * @param {string} donorParams.searchTerm Amount to donate
 * @param {string} donorParams.charityId Selected charity ID
 * @param {number|undefined} donorParams.maxResults The maximum number of results to return
 * @param {string|undefined} donorParams.cancelToken Axios cancel token
 * @param {boolean|undefined} donorParams.skipCache Skip DB cache
 * @returns {Promise}
 */
function getDonors({ searchTerm, charityId, maxResults, cancelToken, signal, skipCache = true }) {
  return apiRequest.doGetRequest(
    `/data/contacts/search`,
    { search_term: searchTerm, charity_id: charityId, max_results: maxResults ?? undefined },
    { 'X-Harness-JWT': getToken() },
    { skipCache },
    cancelToken,
    signal,
  );
}

/**
 * Post donation by client for donor
 * @param {Object} donationParams donation parameters
 * @param {string} charityId Selected charity ID
 * @returns {Promise}
 */
function postClientDonation(donationParams, charityId) {
  const donationFormData = objectToFormData(donationParams);

  return apiRequest.doPostRequest(
    `/charity/${charityId}/donation`,
    donationFormData,
  );
}

/**
 * Post card params to get stripe token
 * @param {Object} cardParams card parameters
 * @returns {Promise}
 */
function postCardToken(cardParams) {
  const cardFormData = objectToFormData(cardParams);

  return apiRequest.doPostRequest(
    `/card/token`,
    cardFormData,
    {
      'X-HARNESS-JWT': getToken(),
    },
  );
}

function getDonationStats(charityId) {
  return apiRequest.doGetRequest(`/data/${charityId}/donation-stats`, {}, { 'X-Harness-JWT': getToken() });
}
function getContactStats(charityId) {
  return apiRequest.doGetRequest(`/data/${charityId}/contact-stats`, {}, { 'X-Harness-JWT': getToken() });
}

function getDonationsByDateRange(charityId, startDate, endDate) {
  return apiRequest.doGetRequest(`/data/${charityId}/donation-stats`, { start_date: startDate, end_date: endDate }, { 'X-Harness-JWT': getToken() });
}

function getDonationTimeSeries(charityId, startDate, endDate) {
  return apiRequest.doGetRequest(`/data/${charityId}/donation-time-series`, { start_date: startDate, end_date: endDate }, { 'X-Harness-JWT': getToken() });
}

export {
  EventsTickets,
  ACHOfflineDonation,
  ImpactAmounts,
  customFields,
  donationFlow,
  notificationSettings,
  createDonor,
  deleteDonor,
  getRecentPayouts,
  getPayoutTransactions,
  patchDonorEmployment,
  patchDonorAddress,
  patchDonorPhoneNumber,
  patchDonorPersonalInfo,
  getCharities,
  patchDonorPhone,
  getCurrentKeyword,
  checkKeywordAvailability,
  registerKeyword,
  deleteKeyword,
  getToken,
  getActivity,
  getPost,
  getPosts,
  getOnePost,
  addPost,
  getVideoUploadToken,
  deletePost,
  getThreads,
  getThread,
  sendMessage,
  checkTasks,
  sendMessageBulk,
  getDonor,
  updateDonor,
  sendResetLink,
  getLists,
  getOneList,
  createList,
  deleteList,
  addUsersToList,
  removeUserFromList,
  getUsersForList,
  inviteAdmin,
  getCharitySubdomain,
  uploadContacts,
  getMetadataColumns,
  getOutbox,
  getFolders,
  createFolder,
  getFolder,
  createTemplate,
  getTemplate,
  getAllTemplates,
  editTemplate,
  deleteTemplate,
  logout,
  getAllCampaigns,
  createCampaign,
  editCampaign,
  deleteCampaign,
  getPusher,
  getUploadUriAndToken,
  confirmPhotoUploaded,
  getPhotoStatus,
  getProfileSettings,
  putProfileSettings,
  getOrganizationSettings,
  putOrganizationSettings,
  getTeammates,
  removeTeammate,
  getOrganizationBillingSettings,
  updateOrganizationBillingCard,
  getOrganizationBillingPlan,
  globalImageUploader,
  getCharityUploadUri,
  uploadImage,
  getAvailableMergeTags,
  getMergeTagPreview,
  getAllAutoMessages,
  updateMessage,
  getWidgetConfig,
  updateWidgetConfig,
  getSecretApiKey,
  EmployerMatchingConfig,
  getGetActiveMonthlyDonations,
  setCampaignCategory,
  getAllCategories,
  createCategory,
  deleteCategory,
  editCategory,
  getDonorDonations,
  getClientDonorCards,
  getDonationFee,
  apiRequest,
  getDonors,
  postClientDonation,
  postCardToken,
  fetchAllFundingSources,
  createNewCart,
  addTicketToCart,
  getCart,
  removeTicketFromCart,
  getDonationStats,
  addContactToCart,
  getContactStats,
  getDonationsByDateRange,
  getDonationTimeSeries,
};
